/**
 * App Component
 * Root component that sets up routing, context providers, and error handling
 */

import { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './components/layout/ErrorBoundary';
import { Layout } from './components/layout/Layout';
import { AuthProvider } from './contexts/AuthContext';
import { UserProfileProvider } from './contexts/UserProfileContext';
import { GridProvider } from './contexts/GridContext';
import { CreditsProvider } from './contexts/CreditsContext';
import { TutorialProvider, useTutorial } from './contexts/TutorialContext';
import { sessionManager } from './services/user/SessionManager';
import { Home } from './pages/Home';
import { CookieBanner } from './components/common/CookieBanner';
import { ConsentGuard } from './components/auth/ConsentGuard';
import { hasConsent, setMarketingConsent } from './services/consent/consentManager';
import { enableMetaPixel } from './services/metaPixel/metaPixelLoader';
import type { Composition } from './types/composition';

// Import test helper for development (exposes window.testConsentGuard)
import './utils/testConsentGuard';

// Lazy load route components for code splitting
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const Contact = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));
const Terms = lazy(() => import('./pages/Terms').then(module => ({ default: module.Terms })));
const FAQ = lazy(() => import('./pages/FAQ').then(module => ({ default: module.FAQ })));
const Explore = lazy(() => import('./pages/Explore').then(module => ({ default: module.Explore })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(module => ({ default: module.AdminDashboard })));
const AdminCompositions = lazy(() => import('./pages/AdminCompositions').then(module => ({ default: module.AdminCompositions })));
const SharedComposition = lazy(() => import('./pages/SharedComposition').then(module => ({ default: module.SharedComposition })));
const MyCompositions = lazy(() => import('./pages/MyCompositions').then(module => ({ default: module.MyCompositions })));
const Profile = lazy(() => import('./pages/Profile').then(module => ({ default: module.Profile })));
const Privacy = lazy(() => import('./pages/Privacy').then(module => ({ default: module.Privacy })));
const EmailConsent = lazy(() => import('./pages/EmailConsent').then(module => ({ default: module.EmailConsent })));
const NotFound = lazy(() => import('./pages/NotFound').then(module => ({ default: module.NotFound })));

// Lazy load landing page component
const LandingPageRoute = lazy(() => import('./pages/landing/LandingPageRoute').then(module => ({ default: module.LandingPageRoute })));

// Lazy load tutorial components for better initial load performance
const TutorialOverlay = lazy(() => import('./components/tutorial/TutorialOverlay').then(module => ({ default: module.TutorialOverlay })));
const TutorialErrorBoundary = lazy(() => import('./components/tutorial/TutorialErrorBoundary').then(module => ({ default: module.TutorialErrorBoundary })));

/**
 * Loading fallback component
 */
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-accent text-sm">Loading...</div>
    </div>
  );
}

/**
 * TutorialWrapper Component
 * 
 * Wraps the TutorialOverlay with an error boundary and lazy loading.
 * Provides access to tutorial context for error handling.
 * Only loads tutorial components when tutorial is active.
 */
function TutorialWrapper() {
  const { isActive, exitTutorial } = useTutorial();

  // Don't render anything if tutorial is not active (lazy loading optimization)
  if (!isActive) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <TutorialErrorBoundary onError={exitTutorial}>
        <TutorialOverlay />
      </TutorialErrorBoundary>
    </Suspense>
  );
}

/**
 * App component
 * Main application component with routing and global state management
 */
function App() {
  // State for shared composition loading
  const [sharedComposition, setSharedComposition] = useState<Composition | null>(null);

  // Cookie banner visibility — show only when no consent decision exists
  const [showCookieBanner, setShowCookieBanner] = useState<boolean>(() => !hasConsent());

  /**
   * Handle cookie banner Accept: store consent, load pixel, hide banner, notify Discord popup
   */
  const handleCookieAccept = useCallback(() => {
    setMarketingConsent(true);
    enableMetaPixel();
    setShowCookieBanner(false);
    window.dispatchEvent(new CustomEvent('cookie-banner-dismissed'));
  }, []);

  /**
   * Handle cookie banner Reject: store rejection, hide banner, notify Discord popup
   */
  const handleCookieReject = useCallback(() => {
    setMarketingConsent(false);
    setShowCookieBanner(false);
    window.dispatchEvent(new CustomEvent('cookie-banner-dismissed'));
  }, []);

  /**
   * Handle shared composition loaded
   * This will be passed to the SharedComposition page
   */
  const handleCompositionLoaded = useCallback((composition: Composition) => {
    setSharedComposition(composition);
  }, []);

  /**
   * Cleanup session manager on unmount
   */
  useEffect(() => {
    return () => {
      sessionManager.cleanup();
    };
  }, []);

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <UserProfileProvider>
              <GridProvider>
                <CreditsProvider>
                  <TutorialProvider>
                    <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        {/* Landing pages - SEO-optimized static pages (no Layout wrapper) */}
                        <Route path="/8-bit-music-generator" element={<LandingPageRoute />} />
                        <Route path="/chiptune-music-maker" element={<LandingPageRoute />} />
                        <Route path="/royalty-free-chiptune-loops" element={<LandingPageRoute />} />
                        <Route path="/game-jam-music" element={<LandingPageRoute />} />
                        
                        {/* All other routes use the Layout wrapper */}
                        <Route path="/*" element={
                          <Layout>
                            <Routes>
                              {/* Home page - not lazy loaded for faster initial render */}
                              <Route path="/" element={
                                <ConsentGuard>
                                  <Home sharedComposition={sharedComposition} />
                                </ConsentGuard>
                              } />
                              
                              {/* Composer page - same as home but with /compose path for deep links */}
                              <Route path="/compose" element={
                                <ConsentGuard>
                                  <Home sharedComposition={sharedComposition} />
                                </ConsentGuard>
                              } />
                              
                              {/* About page - lazy loaded */}
                              <Route path="/about" element={<About />} />
                              
                              {/* Contact page - lazy loaded */}
                              <Route path="/contact" element={<Contact />} />
                              
                              {/* Terms page - lazy loaded */}
                              <Route path="/terms" element={<Terms />} />
                              
                              {/* Privacy Policy page - lazy loaded */}
                              <Route path="/privacy" element={<Privacy />} />
                              
                              {/* FAQ page - lazy loaded */}
                              <Route path="/faq" element={<FAQ />} />
                              
                              {/* Explore page - lazy loaded */}
                              <Route path="/explore" element={<Explore />} />
                              
                              {/* Admin dashboard page - lazy loaded and protected */}
                              <Route path="/admin" element={
                                <ConsentGuard>
                                  <AdminDashboard />
                                </ConsentGuard>
                              } />
                              
                              {/* Admin compositions page - lazy loaded and protected */}
                              <Route path="/admin/compositions" element={
                                <ConsentGuard>
                                  <AdminCompositions />
                                </ConsentGuard>
                              } />
                              
                              {/* My Compositions page - lazy loaded and protected */}
                              <Route path="/my-compositions" element={
                                <ConsentGuard>
                                  <MyCompositions />
                                </ConsentGuard>
                              } />
                              
                              {/* Profile page - lazy loaded and protected */}
                              <Route path="/profile" element={
                                <ConsentGuard>
                                  <Profile />
                                </ConsentGuard>
                              } />
                              
                              {/* Email consent page - lazy loaded and accessible to authenticated users */}
                              <Route path="/consent/email" element={<EmailConsent />} />
                              
                              {/* Shared composition page - lazy loaded */}
                              <Route 
                                path="/share/:id" 
                                element={<SharedComposition onCompositionLoaded={handleCompositionLoaded} />} 
                              />
                              
                              {/* 404 page - lazy loaded */}
                              <Route path="*" element={<NotFound />} />
                            </Routes>
                          </Layout>
                        } />
                      </Routes>
                    </Suspense>
                    
                    {/* Tutorial overlay - renders when tutorial is active */}
                    <TutorialWrapper />

                    {/* Cookie consent banner - shows on first visit */}
                    {showCookieBanner && (
                      <CookieBanner onAccept={handleCookieAccept} onReject={handleCookieReject} />
                    )}
                  </TutorialProvider>
                </CreditsProvider>
              </GridProvider>
            </UserProfileProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
